<template>
<span class="dashboard-kebab" v-if="hasActions" @click.stop>
    <md-dialog-confirm
        :md-title="confirmDuplicateProject.title"
        :md-content-html="confirmContentDuplicate"
        :md-ok-text="confirmDuplicateProject.ok"
        :md-cancel-text="confirmDuplicateProject.cancel"
        @close="duplicateProjectConfirm"
        ref="duplicateDialog">
    </md-dialog-confirm>
    <md-dialog-confirm
        :md-title="confirmDuplicateProject.title"
        :md-content-html="confirmContentDuplicateLegacy"
        :md-ok-text="confirmDuplicateProject.ok"
        :md-cancel-text="confirmDuplicateProject.cancel"
        @close="duplicateProjectConfirm"
        ref="duplicateLegacyDialog">
    </md-dialog-confirm>
    <md-dialog-confirm
        :md-title="confirm.title"
        :md-content-html="confirmContentDelete"
        :md-ok-text="confirm.ok"
        :md-cancel-text="confirm.cancel"
        @close="deleteProjectConfirm"
        ref="deleteDialog">
    </md-dialog-confirm>
    <md-dialog ref="renameDialog" class="kebab-rename-dialog" @open="focusRename">
        <md-dialog-title>Rename project</md-dialog-title>
        <md-dialog-content>
            <form novalidate @submit.stop.prevent="saveRename">
                <md-input-container>
                    <label>Project name</label>
                    <md-input ref="renameInput" v-model="renameTitle" @keydown.esc.native.prevent="cancelRename"></md-input>
                </md-input-container>
            </form>
        </md-dialog-content>
        <md-dialog-actions>
            <md-button class="md-primary" @click="cancelRename">Cancel</md-button>
            <md-button class="md-primary md-raised" :disabled="!renameChanged || renaming" @click="saveRename">Rename</md-button>
        </md-dialog-actions>
    </md-dialog>

    <!-- Legacy tool (Josh, 5-6 Oct 2026): move a legacy project into the current editor, in place. Opened from
         the menu item below or from the "Legacy" badge on the tile / list row (openMove via a ref). -->
    <md-dialog ref="moveDialog" class="kebab-move-dialog">
        <md-dialog-title>Move &ldquo;{{ project.title }}&rdquo; to the current editor?</md-dialog-title>
        <md-dialog-content>
            <p>From now on this project opens in the current Cuelist editor. Nothing is copied and nothing is lost:</p>
            <ul>
                <li>Cues, notes, layers and structure stay as they are</li>
                <li>Your team keeps their access</li>
                <li>Version history and share links keep working</li>
            </ul>
            <p><strong>This move can&rsquo;t be undone.</strong></p>
        </md-dialog-content>
        <md-dialog-actions>
            <md-button class="md-primary" :disabled="moving" @click="cancelMove">Not now</md-button>
            <md-button class="md-primary md-raised" :disabled="moving" @click="confirmMove">{{ moving ? 'Moving…' : 'Move project' }}</md-button>
        </md-dialog-actions>
    </md-dialog>

    <button
        type="button"
        class="kebab-trigger"
        ref="trigger"
        aria-haspopup="true"
        :aria-expanded="open ? 'true' : 'false'"
        aria-label="Project actions"
        @click.stop="toggle"
        @keydown.down.prevent="openMenu"
    >
        <md-icon>more_vert</md-icon>
    </button>

    <ul
        v-if="open"
        class="kebab-menu"
        role="menu"
        ref="menu"
        :style="menuStyle"
        @keydown.esc.prevent="closeMenu"
        @keydown.down.prevent="focusNext(1)"
        @keydown.up.prevent="focusNext(-1)"
    >
        <li v-if="canMove" role="menuitem" tabindex="-1" class="kebab-item" @click="openMove">Move to the current editor&hellip;</li>
        <li v-if="canRename" role="menuitem" tabindex="-1" class="kebab-item" @click="onRename">Rename</li>
        <li v-if="canDuplicate" role="menuitem" tabindex="-1" class="kebab-item" @click="onDuplicate">Duplicate</li>
        <li v-if="canDuplicate && canDelete" role="separator" class="kebab-sep"></li>
        <li v-if="canDelete" role="menuitem" tabindex="-1" class="kebab-item kebab-item-danger" @click="onDelete">Delete</li>
    </ul>
</span>
</template>

<script>
/**
 * Shared per-project actions menu (Rename + Duplicate + Delete) for both the grid tile
 * and the list row. Pin/unpin is a direct on-card control, not in this menu.
 *
 * The dropdown renders position:fixed (positioned from the trigger's
 * bounding rect) so it escapes the card's overflow:hidden clip — Vue 2 has
 * no portal. Closes on outside-click / Escape / scroll.
 */
import { mapGetters } from 'vuex';
import { db } from '@/main';
import firebase from 'firebase/app';
import 'firebase/functions';

// What a refused move tells the owner (convertLegacyProject's refuse codes).
const MOVE_REFUSED = {
    already_current: 'This project is already in the current editor.',
    no_pdf: 'This project\u2019s script PDF couldn\u2019t be read, so it wasn\u2019t moved. Contact support.',
    pdf_unreadable: 'This project\u2019s script PDF couldn\u2019t be read, so it wasn\u2019t moved. Contact support.'
};

const KebabMenu = {
    name: 'DashboardKebabMenu',
    props: ['project'],
    data() {
        return {
            open: false,
            menuStyle: {},
            confirmDuplicateProject: { title: 'Confirm Project Duplication', ok: 'Yes', cancel: 'Cancel' },
            confirm: { title: 'Confirm Project Delete', ok: 'Yes', cancel: 'Cancel' },
            renameTitle: '',
            renaming: false,
            moving: false
        };
    },
    computed: {
        ...mapGetters(['user', 'canCreateProject']),
        // Hide the whole menu when it would be empty: a "⋯" that opens to
        // nothing is a dead affordance. Availability here is structural
        // (role-based), not temporal, so absence is clearer than a disabled icon.
        hasActions() {
            return this.canMove || this.canRename || this.canDuplicate || this.canDelete;
        },
        // Exactly the rule Project Settings uses for its Rename button (Josh, 23 Sept:
        // "the permissions on renaming should not change"): an administrator on the
        // project (the owner, or a collaborator of type administrator) AND the
        // project is editable for them (activeProjectEntitled: paid, legacy-
        // editable, or a Show Pass project). Anyone else never sees the item.
        canRename() {
            if (!this.user || !this.project) return false;
            const collab = (this.project.collaborators || {})[this.user.uid];
            const isAdministrator = this.user.uid === this.project.ownerUID
                || !!(collab && collab.type === 'administrator');
            const entitled = !!(this.user.active_pay || this.project.issrc || this.project.unlocked);
            return isAdministrator && entitled;
        },
        // Legacy = opens in the legacy editor (isNew not true); never the automatic sample projects.
        // Only the owner may move one (the server checks too).
        canMove() {
            return !!(this.user && this.project && this.project.isNew !== true && !this.project.isSample
                && this.user.uid === this.project.ownerUID);
        },
        renameChanged() {
            const t = this.renameTitle.trim();
            return t !== '' && t !== (this.project.title || '');
        },
        canDelete() {
            // The owner can always delete their own project. Deletion is ownership
            // management, not a paid edit action — a non-subscriber can now own a
            // duplicated (standard, read-only) project and must be able to remove it.
            return this.user.uid === this.project.ownerUID;
        },
        canDuplicate() {
            if (this.project.title === 'Sample Project' || !this.canCreateProject) return false;
            if (this.user.uid === this.project.ownerUID) return true;
            const collaborators = this.project.collaborators || {};
            const locked = this.project.duplicateSetting || false;
            return Object.keys(collaborators).some(key => key === this.user.uid && !locked);
        },
        confirmContentDuplicate() {
            return `
                <div>
                    <p>Please confirm you would like to duplicate this project. The following will be copied:</p>
                    <ul>
                        <li>Project settings.</li>
                        <li>Layer information.</li>
                        <li>Cues and all annotations.</li>
                        <li>Structure marks (Act, Scene and Song).</li>
                        <li>The ownership of all layers in the new project will be transferred to the user duplicating the project.</li>
                    </ul>
                </div>`;
        },
        confirmContentDuplicateLegacy() {
            return `
                <div>
                    <p>Please confirm you would like to duplicate this legacy project. The following will be duplicated:</p>
                    <ul>
                        <li>Project settings.</li>
                        <li>Layer information.</li>
                        <li>Cues and all annotations.</li>
                        <li>Structure marks (Act, Scene and Song).</li>
                        <li>The ownership of all layers in the new project will be transferred to the user duplicating the project.</li>
                    </ul>
                    <p><span><b>Note:</b> <span>This action will create a new project using the latest version of Cuelist. Some annotations from your legacy project may not transfer correctly to this new version. Please double check all cues and notes on your new project to confirm their accuracy and completeness. The original version of the project will not be altered during the process.</span></span></p>
                </div>`;
        },
        confirmContentDelete() {
            const numCollaborators = this.project.collaborators ? Object.values(this.project.collaborators).length : 0;
            const numCues = this.project.totalCue || this.project.totalCues || 0;
            const base = `Warning, you are about to delete a project with <strong>${numCollaborators}</strong> collaborators and <strong>${numCues}</strong> cues already written!`;
            const showPassWarning = this.project.unlocked
                ? ' <br/><br/><strong>This is a Show Pass project.</strong> The Show Pass used for it will NOT be refunded — once deleted, that pass is gone and you will need to buy another to start a new Show Pass project.'
                : '';
            return `${base}${showPassWarning} Please confirm you would like to delete this project.`;
        }
    },
    methods: {
        toggle() {
            if (this.open) {
                this.closeMenu();
            } else {
                this.openMenu();
            }
        },
        openMenu() {
            const rect = this.$refs.trigger.getBoundingClientRect();
            const MENU_WIDTH = 160;
            // Right-align the menu under the trigger, kept on-screen.
            let left = rect.right - MENU_WIDTH;
            if (left < 8) left = 8;
            this.menuStyle = {
                position: 'fixed',
                top: `${rect.bottom + 4}px`,
                left: `${left}px`,
                width: `${MENU_WIDTH}px`
            };
            this.open = true;
            this.$nextTick(() => {
                const first = this.$refs.menu && this.$refs.menu.querySelector('.kebab-item');
                if (first) first.focus();
            });
        },
        closeMenu() {
            if (!this.open) return;
            this.open = false;
            if (this.$refs.trigger) this.$refs.trigger.focus();
        },
        focusNext(dir) {
            const items = Array.from(this.$refs.menu.querySelectorAll('.kebab-item'));
            if (!items.length) return;
            const idx = items.indexOf(document.activeElement);
            const next = (idx + dir + items.length) % items.length;
            items[next].focus();
        },
        openMove() {
            if (!this.canMove) return;
            this.closeMenu();
            this.$refs.moveDialog.open();
        },
        cancelMove() {
            if (!this.moving) this.$refs.moveDialog.close();
        },
        async confirmMove() {
            if (this.moving) return;
            this.moving = true;
            try {
                const res = await firebase.functions().httpsCallable('convertLegacyProject')({ projectId: this.project.id });
                const out = (res && res.data) || {};
                if (out.converted) {
                    // The dashboard binding brings the new isNew too; set it now so the badge goes at once.
                    this.$set(this.project, 'isNew', true);
                    this.$refs.moveDialog.close();
                    this.$toast.success(`Moved \u201c${this.project.title}\u201d to the current editor.`);
                } else {
                    this.$refs.moveDialog.close();
                    this.$toast.error(MOVE_REFUSED[out.refuse] || 'The project could not be moved. Nothing was changed.');
                    if (out.refuse === 'already_current') this.$set(this.project, 'isNew', true);
                }
            } catch (error) {
                console.error('Error moving project to the current editor:', error);
                this.$toast.error((error && error.code === 'functions/permission-denied')
                    ? 'Only the project owner can move this project.'
                    : 'The project could not be moved. Nothing was changed.');
            } finally {
                this.moving = false;
            }
        },
        onRename() {
            this.closeMenu();
            this.renameTitle = this.project.title || '';
            this.$refs.renameDialog.open();
        },
        focusRename() {
            this.$nextTick(() => {
                const el = this.$refs.renameInput && this.$refs.renameInput.$el;
                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        cancelRename() {
            this.$refs.renameDialog.close();
        },
        async saveRename() {
            if (!this.renameChanged || this.renaming) return;
            const title = this.renameTitle.trim();
            this.renaming = true;
            try {
                // The same single-field update Project Settings makes.
                await db.collection('projects').doc(this.project.id).update({ title });
                // Projects past the dashboard's first page are fetched once, not
                // bound live, so update what is on screen rather than wait for a
                // reload. (First-page projects get the same value from the binding.)
                this.$set(this.project, 'title', title);
                this.$refs.renameDialog.close();
                this.$toast.success('Project renamed.');
            } catch (error) {
                console.error('Error renaming project:', error);
                this.$toast.error('Could not rename the project. Please try again.');
            } finally {
                this.renaming = false;
            }
        },
        onDuplicate() {
            this.closeMenu();
            if (this.project.isNew) {
                this.$refs.duplicateDialog.open();
            } else {
                this.$refs.duplicateLegacyDialog.open();
            }
        },
        async duplicateProjectConfirm(type) {
            if (type !== 'ok') return;
            try {
                this.$toast.success('Project duplication process started in background!');
                const newProjectID = await this.$store.dispatch('duplicateProject', { project: this.project });
                this.$emit('projectAccepted', newProjectID);
            } catch (error) {
                console.error('Error duplicating project:', error);
                this.$store.dispatch('setDuplicateProjectLoading', false);
                this.$toast.error('Could not duplicate the project. Please try again.');
            }
        },
        onDelete() {
            this.closeMenu();
            this.$refs.deleteDialog.open();
        },
        deleteProjectConfirm(type) {
            if (type !== 'ok') return;
            try {
                this.$store.dispatch('deleteProject', this.project.id);
                this.$emit('projectDeleted', this.project.id);
            } catch (error) {
                console.error('Error deleting project:', error);
                this.$toast.error('Could not delete the project. Please try again.');
            }
        },
        onDocumentClick(e) {
            if (!this.open) return;
            const menu = this.$refs.menu;
            const trigger = this.$refs.trigger;
            if (menu && !menu.contains(e.target) && trigger && !trigger.contains(e.target)) {
                this.closeMenu();
            }
        },
        onScrollOrResize() {
            if (this.open) this.closeMenu();
        }
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick, true);
        window.addEventListener('scroll', this.onScrollOrResize, true);
        window.addEventListener('resize', this.onScrollOrResize);
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick, true);
        window.removeEventListener('scroll', this.onScrollOrResize, true);
        window.removeEventListener('resize', this.onScrollOrResize);
    }
};
export default KebabMenu;
</script>

<style lang="scss">
.kebab-move-dialog .md-dialog { max-width: 460px; }
.kebab-move-dialog .md-dialog-content p { margin: 0 0 10px; }
.kebab-move-dialog .md-dialog-content ul { margin: 0 0 12px; padding-left: 18px; }

.dashboard-kebab {
    .kebab-trigger {
        background: none;
        border: none;
        padding: 4px;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        border-radius: 50%;
        &:hover { background: rgba(0, 0, 0, 0.08); }
        &:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
    }
}
.kebab-menu {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    z-index: 1000;
    .kebab-item {
        padding: 10px 16px;
        cursor: pointer;
        font-size: 14px;
        color: #333;
        &:hover, &:focus { background: #f0f0f0; outline: none; }
        &.kebab-item-danger { color: #d32f2f; }
    }
    .kebab-sep {
        height: 1px;
        background: rgba(0, 0, 0, 0.12);
        margin: 4px 0;
    }
}
.md-theme-show-mode {
    .kebab-menu {
        background: #37474f;
        .kebab-item {
            color: rgba(255, 255, 255, 0.87);
            &:hover, &:focus { background: rgba(255, 255, 255, 0.12); }
            &.kebab-item-danger { color: #ff6e6e; }
        }
        .kebab-sep { background: rgba(255, 255, 255, 0.2); }
    }
}
</style>


// WEBPACK FOOTER //
// kebab-menu.vue?5640b834